import React, { useEffect, useState, useCallback } from 'react';
import { GoogleLogin, googleLogout, GoogleOAuthProvider } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { toast, Toaster } from 'react-hot-toast';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import Header from './components/Header';
import Workspaces from './components/Workspaces';
import EmailTemplates from './components/EmailTemplates';
import Dashboard from './components/Dashboard';
import ProfileLayout from './components/ProfileLayout';
import AdminRenewals from './components/AdminRenewals';
import AdminOrdersDashboard from './components/AdminOrdersDashboard';
import AdminRenewalStatus from './components/AdminRenewalStatus';
import AdminLoginHistory from './components/AdminLoginHistory';
import AdminAfasTransactions from './components/AdminAfasTransactions';
import AdminAfasTransactionDetails from './components/AdminAfasTransactionDetails';
import AdminPeppolInvoices from './components/AdminPeppolInvoices';
import AdminCustomers from './components/AdminCustomers';
import AdminCustomerDetails from './components/AdminCustomerDetails';
import AdminFlexiblePlans from './components/AdminFlexiblePlans';
import AdminGcp from './components/AdminGcp';
import AdminJira from './components/AdminJira';
import AdminUserGuide from './components/AdminUserGuide';
import AdminUsers from './components/AdminUsers';
import GlobalAdminFAB from './components/GlobalAdminFAB';
import PublicOrderPayment from './components/PublicOrderPayment';
import { translations } from './translations';
import { getAppTitle, isDevEnvironment, DEV_TITLE_SUFFIX } from './utils/environment';
import { CAP, can, PERMISSIONS_CHANGED_EVENT } from './utils/permissions';
import { PermissionProvider } from './utils/PermissionContext';
import { setActiveTranslations, activeT, errorText } from './utils/apiErrors';
import { authHeaders } from './utils/apiAuth';


// Google Identity Services issues a one-hour ID token and no refresh token, so
// nothing renews a session on its own. Re-issue a few minutes before expiry.
const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

const tokenExpiresAt = (token) => {
  try {
    const { exp } = jwtDecode(token);
    return exp ? exp * 1000 : 0;
  } catch (e) {
    return 0;
  }
};

function App() {
  const [authUser, setAuthUser] = useState(() => {
    const storedUser = localStorage.getItem('authUser');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [googleIdToken, setGoogleIdToken] = useState(localStorage.getItem('googleIdToken'));

  const [language, setLanguage] = useState('nl');
  const t = translations[language];

  // utils/apiErrors reads this so useApi can translate a refusal it raises
  // itself. Set during render rather than in an effect: an apiRequest fired
  // from a child's mount effect would otherwise beat the effect to it.
  setActiveTranslations(t);

  const apiUrl = process.env.REACT_APP_API_URL || '';
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const [hasUnseenOrders, setHasUnseenOrders] = useState(false);
  const [runTour, setRunTour] = useState(false);

  // Flag non-production builds in the browser tab, so a dev tab is never mistaken
  // for production. The backend skips all live Google writes in the same envs.
  useEffect(() => {
    document.title = getAppTitle();
  }, []);


  useEffect(() => {
    if (!googleIdToken) return;

    let interval;

    const checkUnseenOrders = async () => {
      try {
        const storedCustomer = localStorage.getItem('selectedCustomer');
        if (!storedCustomer) return;
        
        const customerId = JSON.parse(storedCustomer).customerId;
        if (!customerId) return;

        const res = await fetch(`${apiUrl}/api/customers/${customerId}/orders?limit=1&sortBy=createdAt&sortOrder=desc`, {
          headers: authHeaders()
        });

        // This poll ignores failures, so without stopping it a tab whose token
        // died would retry every 30s for as long as it stays open.
        if (res.status === 401) {
          clearInterval(interval);
          return;
        }

        if (res.ok) {
          const data = await res.json();
          const orders = data.orders || [];
          if (orders.length > 0) {
            const latestOrderDate = new Date(orders[0].createdAt).getTime();
            const lastViewed = localStorage.getItem(`lastViewedOrdersAt_${customerId}`);
            
            if (!lastViewed || latestOrderDate > new Date(lastViewed).getTime()) {
              setHasUnseenOrders(true);
            } else {
              setHasUnseenOrders(false);
            }
          }
        }
      } catch (err) {
        console.error("Failed to check unseen orders:", err);
      }
    };

    checkUnseenOrders();
    interval = setInterval(checkUnseenOrders, 30000);
    
    const handleReset = () => checkUnseenOrders();
    window.addEventListener('ordersViewed', handleReset);
    
    // Check when local storage changes (e.g. customer selection changes)
    const handleStorageChange = () => checkUnseenOrders();
    window.addEventListener('storage', handleStorageChange);

    return () => {
        clearInterval(interval);
        window.removeEventListener('ordersViewed', handleReset);
        window.removeEventListener('storage', handleStorageChange);
    };
  }, [googleIdToken, apiUrl]);

  const handleLogout = useCallback(() => {
    googleLogout();
    localStorage.removeItem('googleIdToken');
    localStorage.removeItem('authUser');
    setAuthUser(null);
    setGoogleIdToken(null);
  }, []);

  // `silent` is the background renewal path: it must not blank the app behind a
  // loading screen, and must not sign the user out on a transient failure — the
  // token it is replacing is still valid at that point.
  const verifyTokenWithBackend = useCallback(async (token, { silent = false } = {}) => {
    if (!silent) setIsAuthLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/auth/v1/google/verify-token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.user) {
        setAuthUser(prev => {
          if (JSON.stringify(prev) === JSON.stringify(data.user)) return prev;
          return data.user;
        });
        localStorage.setItem('authUser', JSON.stringify(data.user));
        localStorage.setItem('googleIdToken', token);
        setGoogleIdToken(prev => prev === token ? prev : token);
      } else {
        if (silent) {
          console.warn("Silent token renewal refused:", data.error);
          return;
        }
        // Any refusal must say why. This used to fire only for the exact code
        // `access_denied`, so every other reason — a disabled account, an
        // unregistered domain, a personal Google account — bounced the user
        // back to the sign-in screen with no message at all, which reads as
        // "login is broken" rather than "you are not allowed in".
        //
        // `detail` is where create_json_error puts the sentence; errorText
        // prefers a translation of the code when there is one.
        //
        // activeT() rather than the `t` in scope, so this callback keeps its
        // identity: it sits in the dependency array of the effect that verifies
        // the stored token on mount, and taking `t` would re-verify on every
        // language switch.
        if (data.error) {
          const translations_ = activeT();
          toast.error(
            errorText({ error: data.error, message: data.detail }, translations_,
              translations_?.error_access_denied
                || 'Your account does not have access to this tool.'),
            { id: 'sign-in-refused', duration: 8000 }
          );
        }
        handleLogout();
      }
    } catch (error) {
      console.error("Token verification failed:", error);
      if (!silent) handleLogout();
    } finally {
      if (!silent) setIsAuthLoading(false);
    }
  }, [handleLogout, apiUrl]);

  useEffect(() => {
    const storedToken = localStorage.getItem('googleIdToken');
    if (storedToken) {
      verifyTokenWithBackend(storedToken);
    } else {
      setIsAuthLoading(false);
    }
  }, [verifyTokenWithBackend]);

  // auto_select re-issues a credential without any UI while the user's Google
  // session is alive. If it cannot, nothing happens and the existing
  // logout-on-401 path still applies — this only ever improves on that.
  const renewIdToken = useCallback(() => {
    const googleId = window.google?.accounts?.id;
    if (!googleId || !googleClientId) return;

    googleId.initialize({
      client_id: googleClientId,
      auto_select: true,
      callback: (response) => {
        if (response?.credential) {
          verifyTokenWithBackend(response.credential, { silent: true });
        }
      },
    });
    googleId.prompt();
  }, [googleClientId, verifyTokenWithBackend]);

  useEffect(() => {
    if (!googleIdToken) return;
    const expiresAt = tokenExpiresAt(googleIdToken);
    if (!expiresAt) return;

    const renewIfDue = () => {
      if (Date.now() >= expiresAt - TOKEN_REFRESH_MARGIN_MS) renewIdToken();
    };

    // The timer alone is not enough: a background tab throttles it and a sleeping
    // machine never fires it, so re-check whenever the tab comes back instead.
    const timer = setTimeout(
      renewIfDue,
      Math.max(expiresAt - TOKEN_REFRESH_MARGIN_MS - Date.now(), 0)
    );
    const onVisible = () => {
      if (document.visibilityState === 'visible') renewIfDue();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [googleIdToken, renewIdToken]);

  // The role is resolved server-side on every request, so a SuperAdmin can
  // change someone's access while they have the app open. useApi raises this
  // event when a refusal says the cached profile is stale; re-verifying pulls
  // the new capabilities and the UI stops offering what now 403s.
  useEffect(() => {
    const handlePermissionsChanged = () => {
      const storedToken = localStorage.getItem('googleIdToken');
      if (storedToken) {
        verifyTokenWithBackend(storedToken);
      } else {
        handleLogout();
      }
    };

    window.addEventListener(PERMISSIONS_CHANGED_EVENT, handlePermissionsChanged);
    return () => window.removeEventListener(PERMISSIONS_CHANGED_EVENT, handlePermissionsChanged);
  }, [verifyTokenWithBackend, handleLogout]);

  const handleGoogleLoginSuccess = (credentialResponse) => {
    const token = credentialResponse?.credential;
    if (token) {
      try {
        jwtDecode(token);
        verifyTokenWithBackend(token);
      } catch (e) {
        console.error('Invalid token received.');
      }
    }
  };

  // Gates a route on a capability rather than a role name. Defaults to
  // viewAdminScreens, which is what every admin screen needs, so only the
  // exceptions state a capability explicitly. Cosmetic only — every endpoint
  // behind these routes is authorized server-side too.
  const ProtectedRoute = ({ children, capability = CAP.VIEW_ADMIN_SCREENS }) => {
    if (!can(authUser, capability)) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  const renderAuthenticatedApp = () => {
    if (isAuthLoading) {
      return <div className="flex items-center justify-center h-screen bg-gray-100"><p>{t.loading}</p></div>;
    }

    if (!authUser) {
      return (
        <GoogleOAuthProvider clientId={googleClientId}>
          <Toaster position="top-center" />
          <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
            <h2 className="text-3xl font-bold mb-4">{t.login_title}</h2>
            {/* Non-prod marker, so the environment is clear before signing in and acting.
                Same constant as the header and the browser tab title. */}
            {isDevEnvironment() && (
              <span className="mb-4 px-3 py-1 rounded border border-amber-300 bg-amber-100 text-amber-900 text-sm font-semibold">
                {DEV_TITLE_SUFFIX}
              </span>
            )}
            <p className="mb-6">{t.login_message}</p>
            <GoogleLogin
              onSuccess={handleGoogleLoginSuccess}
              onError={(err) => {
                console.error(err);
                toast.error(err?.detail);
              }}
            />
          </div>
        </GoogleOAuthProvider>
      );
    }

    return (
      <PermissionProvider authUser={authUser}>
        <Toaster position="bottom-right" />
        <GlobalAdminFAB authUser={authUser} />
        <div className="min-h-screen bg-gray-100">
          <Header
            authUser={authUser}
            onLogout={handleLogout}
            language={language}
            onLanguageChange={setLanguage}
            t={t}
            hasUnseenOrders={hasUnseenOrders}
            onSummonTour={() => setRunTour(true)}
          />
          <main className="p-4 sm:p-8">
            <Routes>
              <Route path="/" element={
                <Dashboard
                  authUser={authUser}
                  t={t}
                  language={language}
                  onLanguageChange={setLanguage}
                  handleLogout={handleLogout}
                  googleIdToken={googleIdToken}
                  runTour={runTour}
                  setRunTour={setRunTour}
                />
              } />
              <Route path="/dashboard" element={
                <Dashboard
                  authUser={authUser}
                  t={t}
                  language={language}
                  onLanguageChange={setLanguage}
                  handleLogout={handleLogout}
                  googleIdToken={googleIdToken}
                  runTour={runTour}
                  setRunTour={setRunTour}
                />
              } />
              <Route
                path="/workspaces"
                element={
                  <ProtectedRoute>
                    <Workspaces t={t} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/email-templates"
                element={
                  <ProtectedRoute>
                    <EmailTemplates t={t} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/renewals-summary"
                element={
                  <ProtectedRoute>
                    <AdminRenewals t={t} language={language} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/renewal-status"
                element={
                  <ProtectedRoute>
                    <AdminRenewalStatus googleIdToken={googleIdToken} t={t} language={language} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/orders-dashboard"
                element={
                  <ProtectedRoute>
                    <AdminOrdersDashboard googleIdToken={googleIdToken} t={t} language={language} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/login-history"
                element={
                  <ProtectedRoute>
                    <AdminLoginHistory googleIdToken={googleIdToken} t={t} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/afas-transactions"
                element={
                  <ProtectedRoute>
                    <AdminAfasTransactions t={t} language={language} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/afas-transactions/:voucherNr"
                element={
                  <ProtectedRoute>
                    <AdminAfasTransactionDetails t={t} language={language} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/peppol-invoices"
                element={
                  <ProtectedRoute>
                    <AdminPeppolInvoices t={t} language={language} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/customers"
                element={
                  <ProtectedRoute>
                    <AdminCustomers googleIdToken={googleIdToken} t={t} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/customers/:id"
                element={
                  <ProtectedRoute>
                    <AdminCustomerDetails googleIdToken={googleIdToken} t={t} language={language} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/flexible-plans"
                element={
                  <ProtectedRoute>
                    <AdminFlexiblePlans googleIdToken={googleIdToken} t={t} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/gcp"
                element={
                  <ProtectedRoute>
                    <AdminGcp googleIdToken={googleIdToken} t={t} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/jira"
                element={
                  <ProtectedRoute>
                    <AdminJira googleIdToken={googleIdToken} t={t} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/user-guide"
                element={
                  <ProtectedRoute>
                    <AdminUserGuide t={t} />
                  </ProtectedRoute>
                }
              />
              {/* The one screen an Admin cannot reach: who has access. */}
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute capability={CAP.MANAGE_USERS}>
                    <AdminUsers googleIdToken={googleIdToken} authUser={authUser} t={t} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile/*"
                element={
                  <ProfileLayout
                    t={t}
                    hasUnseenOrders={hasUnseenOrders}
                  />
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </PermissionProvider>
    );
  };

  return (
    <Router>
      <Routes>
        <Route
          path="/payment/:orderId"
          element={
            <PublicOrderPayment
              language={language}
              onLanguageChange={setLanguage}
              t={t}
            />
          }
        />
        <Route path="/*" element={renderAuthenticatedApp()} />
      </Routes>
    </Router>
  );
}

export default App;

